/* global G */
import { v4 as uuidV4 } from 'uuid'
import { curry, getKey, setKey } from 'lib/util'
import { actionWithHooks } from 'lib/sequence/module/action'
import refresh from 'lib/sequence/model/api/refresh'
import sequenceComponentFind from 'lib/sequence/component/children/find'
import sequenceComponentState from 'lib/sequence/component/state'
import { get } from 'lib/sequence/component/state/value'
import { show } from 'lib/sequence/component/state/hidden'
import setStepTab, { CONFIRM, LIST } from 'app/_shared/events/setStepTab'
import { expose } from 'app/_shared/events/device/find'
import { settings } from 'app/_shared/session'
import undefinedModelLabel from 'app/_shared/events/device/undefinedModelLabel'
import setSerialFieldState from 'app/_shared/events/device/setSerialFieldState'

const {
  get: getError,
} = sequenceComponentState('error')

/**
 * Attempts to obtain the product with key `event.detail.key` and sets it as the product
 * property of the current model.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component the current action's main component
 * @param {Event|Object} eventOrItem      the event object that triggered this handler
 * @param {Object} eventOrItem.detail     custom event details
 * @returns {Promise<void>} void
 */
// eslint-disable-next-line no-unused-vars
const setItem = async (module, component, eventOrItem) => {
  const model = module[G.MODEL]
  const { itemData, item, product, equipment, itemInstalledAt } = model[G.CHILDREN]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { serial: serialField, btnMore, existingHelp } = sequenceComponentFind(actionComponent)
  const { item: selectedItem = eventOrItem } = eventOrItem?.detail || {}
  const found = expose(selectedItem) || {}

  const serial = get(serialField)

  // store inserted serial and found name
  itemData[G.CACHE] = { value: {
    serial: found.serviceItem?.value?.serial || serial,
    numberCommission: found.serviceItem?.value?.numberCommission,
    serialDistinction: found.serviceItem?.value?.serialDistinction,
    name: found.name || await undefinedModelLabel(module),
  } }

  if (found.serviceItem) {
    // save found service item
    await refresh(item, found.serviceItem)

    found.installedAt
      && await refresh(itemInstalledAt, found.installedAt)

    const serviceItemExists = getKey(G.REF, item[G.STATE])
      || found.serviceItem?._rev && found.serviceItem._rev?.charAt(0) !== '0'

    serviceItemExists
      ? setKey(false, G.BULK, item[G.STATE])
      : setKey(true, G.BULK, item[G.STATE])
        && setKey(uuidV4(), G.REF, item[G.STATE])
        && setKey(50, 'status', item[G.DATA])

    // save found equipment
    found.equipment
      && await refresh(equipment, found.equipment)
    // save found product
    found.product
      && await refresh(product, found.product)

    setStepTab(CONFIRM)(module, component, eventOrItem)
  } else if (settings.suppressEquipmentDevices && serial && !getError(serialField)) {
    const nextTab = settings.suppressProductDevices ? CONFIRM : LIST
    await refresh(item, itemData[G.CACHE])
    await setStepTab(nextTab)(module, component, eventOrItem)
  } else if (settings.suppressProductDevices && !serialField[G.STATE].identify) {
    await refresh(item, itemData[G.CACHE])
    show(existingHelp)
    !settings.serialFieldConfig?.strict
      && show(btnMore)
  } else {
    show(existingHelp)
    !settings.serialFieldConfig?.strict
      && show(btnMore)
  }

  setSerialFieldState(module, serialField, [serial, found])

  await actionWithHooks(module[G.STATE][G.ACTION])([])
}

export default curry(setItem)
